/**
 * Whether a stored registration session conflicts with the `?invite=` link the
 * reader just followed (Jeff, Oct 1 — a Team Budget slot held for them).
 *
 * An invite binds when a session is CREATED: the server writes it onto the new
 * session and never onto one that already exists. So a stored session that does
 * not already hold this invite can never come to hold it — the reader either keeps
 * that session (and the invite waits for them), or starts a new one that holds it.
 *
 * Decided where a session resumes (`index.jsx`), ahead of the `?team_id=` check
 * (`sessionLinkConflict`), which still runs on whatever session is then applied.
 * "Something to lose" means what it means there: progress past the start step, a
 * team, or a team registration in progress.
 */

import type { ConflictSession } from './sessionLinkConflict'
import { PROGRESSED_STEPS } from './sessionLinkConflict'

/** What `index.jsx` does with the stored session. */
export type SessionInviteConflict =
  /** No invite in the URL, or no stored session: carry on as usual. */
  | 'none'
  /** The session already holds this invite: drop the param and resume it. */
  | 'consume'
  /** The session has nothing to lose: open a new one that holds the invite. */
  | 'replace'
  /** The session has something to lose: ask before starting over with the invite. */
  | 'prompt'

/** The stored-session fields this decision reads. */
export interface InviteConflictSession extends ConflictSession {
  /**
   * The invite the session holds, as the server describes it — absent when it holds
   * none, or when the one it held was cancelled, changed or used meanwhile.
   */
  invite?: { id?: string | null } | null
}

export const sessionInviteConflict = ({
  urlInvite,
  session,
  teamOfficialTypeId,
}: {
  urlInvite: string | null | undefined
  session: InviteConflictSession | null | undefined
  /** The team-official registration type's id, when the types have loaded. */
  teamOfficialTypeId?: string | null
}): SessionInviteConflict => {
  if (!urlInvite || !session) {
    return 'none'
  }
  // A bearer token: compared exactly, never case-folded.
  if (session.invite?.id === urlInvite) {
    return 'consume'
  }
  const registeringTeam = Boolean(
    teamOfficialTypeId &&
      session.registration_type_id != null &&
      String(session.registration_type_id) === String(teamOfficialTypeId)
  )
  const progressed = Boolean(
    session.registration_step && PROGRESSED_STEPS.has(session.registration_step)
  )

  return registeringTeam || progressed || session.team_id ? 'prompt' : 'replace'
}
